import {
  Component,
  ChangeDetectionStrategy,
  Injector,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { CommitteeService, DocumentService, UserService } from '@src/core/services';
import { UserUI, ViewMode } from '@src/models';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { ObjectId } from '@src/types/id';
import { Nullable } from '@src/types/utils';
import { isFileContact, reload } from '@src/utils';
import { SessionService } from '@src/app/modules/auth';
import { BaseResizableComponent } from '@src/app/modules/base';
import { PhotoService } from '@src/app/modules/photo';
import { InternalMessengerService } from '@src/app/modules/internal-messenger';

import { PermissionService } from './services';

@Component({
  selector: 'app-user-info',
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserInfoComponent extends BaseResizableComponent implements OnChanges {
  private readonly internalMessengerService = inject(InternalMessengerService);
  private readonly committeeService = inject(CommitteeService);
  private readonly photoService = inject(PhotoService);
  private readonly documentService = inject(DocumentService);
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);
  private readonly session = inject(SessionService);
  private readonly permissionService = inject(PermissionService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);

  @Input() mode: ViewMode = 'view';
  @Input() userId?: Nullable<ObjectId>;
  @Input() organisationId?: string | null;
  @Input() allowDeleting = false;
  @Input() allowExcluding = false;
  @Input() searchByPhone = true;
  @Input() searchByEmail = true;
  @Input() isParentOrganisation = false;
  @Output() saved = new EventEmitter<number | string>(); // TODO: Fix type
  @Output() canceled = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<UserUI>();
  @Output() excluded = new EventEmitter<UserUI>();

  loading = false;

  readonly data$ = this.userService.user$;
  readonly committeesList$ = this.committeeService.committeesForUser$;

  private readonly confirmCancelEditingDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.undoEditHeader'),
      size: 's',
      closeable: false,
    },
  );

  constructor() {
    super();

    this.data$.pipe(takeUntil(this.destroyed$$)).subscribe(user => {
      if (user?.id) {
        this.committeeService.getCommitteesByUserId(user.id);
      } else {
        this.committeeService.resetCommitteesForUser();
      }
      this.loading = !user;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.userId) {
      this.userService.resetUser();
      if (!!this.userId) {
        this.mode = 'view';
        this.userService.loadUser(this.userId);
        this.permissionService.getPermissions(this.organisationId, this.userId);
      }
    }

    if (changes.organisationId) {
      this.permissionService.getPermissions(this.organisationId, this.userId);
    }

    if (changes.mode) {
      if (this.mode === 'create') {
        this.userService.resetUser();
      }
    }

    this.cdr.markForCheck();
  }

  onStartEditing() {
    this.mode = 'edit';
  }

  async onSaveData(data: UserUI): Promise<void> {
    if (this.mode === 'edit' || data.organisationsToAdd) {
      this.updateUser(data);
    } else if (this.mode === 'create') {
      this.createUser(data);
    }
  }

  onCancel(): void {
    this.confirmCancelEditingDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          this.canceled.emit();
          if (this.userId) {
            this.userService.resetUser();
            this.userService.loadUser(this.userId);
          }

          this.mode = 'view';
        }
      },
    });
  }

  private createUser(data: UserUI): void {
    this.userService
      .createUser(data)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(async res => {
        const newUserId = res.id;
        if (newUserId) {
          if (data.photo) {
            await this.uploadPhoto(data.photo, newUserId, true);
          }

          if (data.paymentDetails) {
            await this.uploadDocument(data.paymentDetails, 16, newUserId); // 16 Письмо с реквизитами для пользователя
          }

          if (data.contacts) {
            const promises = data.contacts.map(async contact => {
              if (isFileContact(contact.contactTypeId)) {
                if (contact.oldDocument?.id) {
                  contact.contact = contact.oldDocument.id;
                }

                if (contact.newDocument) {
                  contact.contact = await this.uploadDocument(contact.newDocument, 10); // 10 Документ
                }

                contact.oldDocument = undefined;
                contact.newDocument = undefined;
              }
              return contact;
            });
            await Promise.all(promises);

            await this.userService.replaceUserContacts({ userId: newUserId, contacts: data.contacts });
          }
        }

        this.saved.emit(newUserId);

        this.mode = 'view';
        this.cdr.markForCheck();
      });
  }

  private updateUser(data: UserUI): void {
    const isUserNameChanged = this.data$.value && this.isUserNameChanged(this.data$.value, data);

    this.userService
      .editUser(data)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(async res => {
        const userId = res.id;
        if (userId) {
          if (data.photo) {
            await this.uploadPhoto(data.photo, userId, true);
            this.internalMessengerService.userAvatarChanged(userId).subscribe();
          }

          if (data.paymentDetails) {
            await this.uploadDocument(data.paymentDetails, 16, userId); // 16 Письмо с реквизитами для пользователя
          }

          if (data.deletePaymentDetailsId) {
            await lastValueFrom(this.deleteDocument(data.deletePaymentDetailsId));
          }

          if (data.contacts) {
            const promises = data.contacts.map(async contact => {
              if (isFileContact(contact.contactTypeId)) {
                if (contact.oldDocument?.id) {
                  contact.contact = contact.oldDocument.id;
                }

                if (contact.newDocument) {
                  contact.contact = await this.uploadDocument(contact.newDocument, 10); // 10 Документ
                }

                contact.oldDocument = undefined;
                contact.newDocument = undefined;
              }
              return contact;
            });
            await Promise.all(promises);

            await this.userService.replaceUserContacts({ userId, contacts: data.contacts });
          }

          this.userService.resetUser();
          this.userService.loadUser(userId);

          if (isUserNameChanged) {
            this.internalMessengerService.userNameChanged(userId).subscribe();
          }
        }

        this.saved.emit(userId);

        this.mode = 'view';
        this.cdr.markForCheck();

        // TODO: START
        // временно решили брать только роль дефолтной организации по задаче #10370 (ждем когда бэк будет работать с несколькими ролями)
        const authUser = this.userService.authUser$.value;
        const defaultOrganisationId = data.organisations?.find(org => org.isDefault)?.organisationId;
        if (
          authUser &&
          authUser.id === data.id &&
          authUser.organisationId !== defaultOrganisationId &&
          authUser.parentOrganisationId
        ) {
          this.session.changeToken(authUser.parentOrganisationId).subscribe(() => reload());
        }
        // TODO: END
      });
  }

  private async uploadPhoto(photo: File, attachId: string, isCover: boolean) {
    if (!photo) return;

    return lastValueFrom(this.photoService.uploadPhoto(photo, attachId, isCover));
  }

  private async uploadDocument(document: File, documentType?: number, attachId?: string): Promise<string | undefined> {
    if (!document) return;

    const documentList = await lastValueFrom(this.documentService.addDocuments([document], attachId, documentType));

    return documentList?.files?.[0].id;
  }

  private deleteDocument(documentId: string) {
    return this.documentService.deleteDocuments([documentId])[0];
  }

  private isUserNameChanged(oldData: UserUI, newData: UserUI) {
    return (
      oldData.firstName?.trim() !== newData.firstName?.trim() ||
      oldData.lastName?.trim() !== newData.lastName?.trim() ||
      oldData.middleName?.trim() !== newData.middleName?.trim()
    );
  }
}
